import React, { ComponentProps, forwardRef, useImperativeHandle, useState } from "react";
import { createPortal } from "react-dom";

type IModalProps = {
	children: React.ReactNode;
	customButton?: React.ReactNode;
	buttonText?: string;
	isDraggable?: boolean;
} & ComponentProps<"div">;

export interface IModalHandle {
	openModal: () => void;
	closeModal: () => void;
}

import { eventActions } from "@src/store";
import styles from "./Modal.module.scss";
import { useAppDispatch } from "@src/store/hooks";
import { ModalDragContext, useModalDrag } from "./useModalDrag";

const ModalFRRF: React.ForwardRefRenderFunction<IModalHandle, IModalProps> = (
	{ children, customButton, buttonText = "Open modal", style, isDraggable = true, ...rest },
	ref,
) => {
	const [isOpen, setIsOpen] = useState(false);
	const dispatch = useAppDispatch();
	const { contentRef, dragProps, resetPosition } = useModalDrag(isDraggable);

	const openModal = () => {
		resetPosition();
		setIsOpen(true);
	};
	const closeModal = () => {
		dispatch(eventActions.showComingSoon({ showComingSoon: false }));
		resetPosition();
		setIsOpen(false);
	};

	useImperativeHandle(ref, () => ({
		openModal,
		closeModal,
	}));

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	return (
		<>
			{!buttonText && (
				<span className={styles.openContainer} onClick={openModal} onKeyDown={() => void 0}>
					{buttonText}
				</span>
			)}
			{customButton}
			{isOpen &&
				createPortal(
					<div className={styles.overlay} onClick={handleOverlayClick} onKeyDown={() => void 0} {...rest}>
						<div ref={contentRef} style={style} className={styles.content} {...dragProps}>
							<ModalDragContext.Provider value={isDraggable}>{children}</ModalDragContext.Provider>
						</div>
					</div>,
					document.querySelector("#modal") as HTMLElement,
				)}
		</>
	);
};

export const Modal = forwardRef(ModalFRRF);
